import React, { PropsWithChildren, useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { CreateURLParams } from "../const";
import { dataStore } from "../../../../../contexts/DataContext";
import useResourceSeriesFlowOverride from "../../../../../components/Player/ResourceSeriesFlowOverride/useResourceSeriesFlowOverride";
import {
    Activity,
    ActivityShell,
    Objective,
} from "@evidenceb/gameplay-interfaces";
import { defineMessages, useIntl } from "react-intl";
import {
    getActivityById,
    getExerciseById,
    getItemDescription,
} from "../../../../../utils/dataRetrieval";
import { parse } from "@evidenceb/athena-common/modules/HTMLParser";
import { CreateSteps } from "../../type";
import ResourceSeriesProvider, {
    useResourceSeriesDispatch,
} from "../../../../../components/Player/ResourceSeries/Contexts";
import { placeholderResourceSeries } from "../../../../../components/Player/ResourceSeries/utils";
import { defaultResourceSeriesReducer } from "../../../../../components/Player/ResourceSeries/utils/defaultReducer";
import {
    ResourceSeries,
    ResourceSeriesExecutionStage,
    ResourceSeriesRecord,
} from "../../../../../components/Player/ResourceSeries/types";
import { TotalItemsAdditionalInformation } from "../../../../../components/Player/ResourceSeries/utils/AdditionalInfoTypes";
import { LearningSetTypes } from "../../../../../interfaces/AthenaResources";
import { Data } from "../../../../../interfaces/Data";
import { useTuto } from "../../../Tuto/useTutos";
import { useData } from "../../../../../hooks/useData";
import { Tuto } from "../../../Tuto/type";
import { MediaResourceItem } from "../../../types";
import { useWorkshop } from "../../../Workshop/useWorkshops";
import { Workshop } from "../../../Workshop/types";
import { CollectionResourcePath, ResourcePath } from "../utils";
import { Exercise } from "@evidenceb/athena-common/interfaces/Exercise";
import { useExercises } from "../../../../../hooks/queries/useExercisesQuery";

const SingleResourceSeriesProvider = ({
    children,
    step,
}: PropsWithChildren<{ step: CreateSteps }>) => {
    const { data } = useContext(dataStore);
    const params = useParams<CreateURLParams>();

    const { addDisplay, removeDisplay } = useResourceSeriesFlowOverride();
    const displayId = useRef<string | undefined>(undefined);
    useEffect(() => {
        const itemId = getItemId(params);
        const activityId = getActivityId(params);
        const objectiveId = getObjectiveId(params);

        if (itemId) {
            if (displayId.current) {
                removeDisplay(displayId.current);
                displayId.current = undefined;
            }
        } else if (activityId || objectiveId) {
            if (displayId.current) removeDisplay(displayId.current);
            let items: (Activity | Objective | undefined)[] = [];
            items.push(data.objectives?.find((obj) => obj.id === objectiveId));
            if (activityId) {
                items.push(
                    data.activities?.find((act) => act.id === activityId)
                );
            }
            const id = addDisplay(
                <div className="display-infos">
                    {items.map((it, index) => (
                        <ItemDisplay item={it} key={index} />
                    ))}
                </div>,
                {
                    priority: "first",
                }
            );
            displayId.current = id;
        } else {
            if (displayId.current) removeDisplay(displayId.current);
            const Placeholder =
                step === CreateSteps.select
                    ? SelectPlaceholder
                    : OrganizePlaceholder;
            const id = addDisplay(<Placeholder />, { priority: "first" });
            displayId.current = id;
        }
    }, [step, addDisplay, removeDisplay, data, params]);

    return (
        <ResourceSeriesProvider
            initialResourceSeries={placeholderResourceSeries(
                params.resourceId1 ?? ""
            )}
            resourceSeriesReducer={(state, action) => {
                switch (action.type) {
                    case "escHatch-updateResourceSeries":
                        return defaultResourceSeriesReducer(state, action);
                    default:
                        return state;
                }
            }}
        >
            <SingleResourceSeriesSideEffect>
                {children}
            </SingleResourceSeriesSideEffect>
        </ResourceSeriesProvider>
    );
};
export default SingleResourceSeriesProvider;

const messages = defineMessages({
    selectResource: {
        id: "playlist-selectResourceToSeePreview",
        defaultMessage: "Choose a resource to preview",
    },
    nothingToSee: {
        id: "playlist-organize-nothingToSeeHere",
        defaultMessage: "Nothing to see here",
    },
    noResourcesToOrganize: {
        id: "playlist-organize-noResourcesToOrganize",
        defaultMessage: "You haven't added any resource to organize yet",
    },
});

const ItemDisplay = ({ item }: { item: Activity | Objective | undefined }) => {
    return (
        <div className="display-block">
            <div className="display__title">
                {item && parse(item.title.long)}
            </div>
            <div className="display__description">
                {item && parse(getItemDescription(item, "teacher"))}
            </div>
        </div>
    );
};

export const SelectPlaceholder = () => {
    const intl = useIntl();

    return (
        <div className="edit-playlist__player-placeholder">
            <p>{intl.formatMessage(messages.selectResource)}</p>
        </div>
    );
};

export const OrganizePlaceholder = () => {
    const intl = useIntl();

    return (
        <div className="edit-playlist__player-placeholder">
            <img
                src="/icons/illustrations/placeholder_playlist_noresource.svg"
                alt=""
            />
            <p>
                <strong>{intl.formatMessage(messages.nothingToSee)}</strong>
            </p>
            <p>{intl.formatMessage(messages.noResourcesToOrganize)}</p>
        </div>
    );
};

const SingleResourceSeriesSideEffect = ({ children }: PropsWithChildren) => {
    const data = useData();
    const exercises = useExercises();
    const params = useParams<CreateURLParams>();

    let resource: ResourcePath | undefined;
    try {
        resource = ResourcePath.fromCreateUrl(params);
    } catch {
        resource = undefined;
    }
    const setId =
        resource instanceof CollectionResourcePath ? resource.setId : undefined;

    const { data: tuto } = useTuto(
        resource?.type === LearningSetTypes.Tutorial ? setId : undefined
    );
    const { data: workshop } = useWorkshop(
        resource?.type === LearningSetTypes.Workshop ? setId : undefined
    );

    const dispatch = useResourceSeriesDispatch<
        ResourceSeriesRecord,
        TotalItemsAdditionalInformation
    >();
    useEffect(() => {
        let nextResourceSeries: ResourceSeries | undefined;
        if (params.resourceType === LearningSetTypes.Module)
            nextResourceSeries = getModuleResourceSeries(
                params,
                data,
                exercises
            );
        if (
            (params.resourceType === LearningSetTypes.Tutorial && tuto) ||
            (params.resourceType === LearningSetTypes.Workshop && workshop)
        ) {
            nextResourceSeries = getResourceSeries((tuto ?? workshop)!);
        }

        if (nextResourceSeries)
            dispatch({
                type: "escHatch-updateResourceSeries",
                value: nextResourceSeries,
            });
    }, [params, data, exercises, dispatch, tuto, workshop]);

    return <>{children}</>;
};

const getModuleResourceSeries = (
    params: CreateURLParams,
    data: Data,
    exercises: Exercise[]
): undefined | ResourceSeries => {
    const moduleId = params.resourceId1;
    const objectiveId = params.resourceId2;
    const activityId = params.resourceId3;
    const exerciseId = params.resourceId4;
    if (!exerciseId || !moduleId || !activityId || !objectiveId)
        return undefined;

    const { type, Gameplay, ...exercise } = getExerciseById(
        exerciseId,
        exercises
    );
    const activity = getActivityById(activityId, data);
    return {
        entryLearningSetId: moduleId,
        results: [],
        executionStage: ResourceSeriesExecutionStage.PlayingCurrentExercise,
        resources: {
            [exercise.id]: {
                id: exercise.id,
                type: "item",
                learning_item_type: type,
                learning_item_data: exercise,
                learning_item_context: undefined,
                shell: activity.shell ?? ActivityShell.Wizard,
            },
        },
        hierarchy: [exerciseId, activityId, objectiveId, moduleId],
        additionalGPProps: {
            options: {
                hideValidationButton: true,
            },
        },
        progression: 0,
        _prevTriesResults: [],
        currentTry: 1,
        comingNext: undefined,
        additionalInformation: {
            totalItems: 1,
        },
    };
};

const getResourceSeries = (
    resources: Record<string, Tuto | Workshop | MediaResourceItem>
): ResourceSeries | undefined => {
    if (!resources) return undefined;
    const setId = Object.values(resources).find(
        ({ type }) => type === "set"
    )!.id;
    const item = Object.values(resources).find(
        ({ type }) => type === "item"
    ) as MediaResourceItem;
    return {
        entryLearningSetId: setId,
        results: [],
        executionStage: ResourceSeriesExecutionStage.PlayingCurrentExercise,
        resources: {
            [item.id]: {
                ...item,
                shell: ActivityShell.Wizard,
            },
        },
        hierarchy: [item.id, setId],
        additionalGPProps: {
            options: {
                hideValidationButton: true,
            },
        },
        progression: 0,
        _prevTriesResults: [],
        currentTry: 1,
        comingNext: undefined,
        additionalInformation: {
            totalItems: 1,
        },
    };
};

const getItemId = (params: CreateURLParams): string | undefined => {
    if (params.resourceType === LearningSetTypes.Module)
        return params.resourceId4;
    return params.resourceId1;
};
const getActivityId = (params: CreateURLParams): string | undefined =>
    params.resourceType === LearningSetTypes.Module
        ? params.resourceId3
        : undefined;
const getObjectiveId = (params: CreateURLParams): string | undefined =>
    params.resourceType === LearningSetTypes.Module
        ? params.resourceId2
        : undefined;
